import * as React from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

type ConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => Promise<void> | void;
  loading?: boolean;
  confirmVariant?: ButtonProps['variant'];
  /**
   * When set, the confirm button stays disabled until the user types this exact
   * text. Reserve it for actions that destroy data irreversibly.
   */
  confirmationPhrase?: string;
};

export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  loading,
  confirmVariant = 'danger',
  confirmationPhrase,
}: ConfirmDialogProps) => {
  const [typed, setTyped] = React.useState('');

  // Reset between openings so a previous confirmation cannot carry over.
  React.useEffect(() => {
    if (!open) {
      setTyped('');
    }
  }, [open]);

  const canConfirm = !confirmationPhrase || typed === confirmationPhrase;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {confirmationPhrase ? (
          <DialogBody>
            <Field
              label={`Type "${confirmationPhrase}" to confirm`}
              htmlFor="confirmation-phrase"
            >
              <Input
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                autoComplete="off"
              />
            </Field>
          </DialogBody>
        ) : null}

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            loading={loading}
            disabled={!canConfirm}
            onClick={() => void onConfirm()}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
